import React, { FC, useMemo, useState } from "react"

import { Badge, Button, Card, Center, Group, ScrollArea, Stack, Table, Text, ThemeIcon } from "@mantine/core"
import { IconActivity, IconBug, IconDoorEnter, IconTimelineEventText, IconUpload } from "@tabler/icons-react"
import formatBytes from "../../assets/formatBytes"

import { HealthDocument, useHealth } from "../../../../contexts/health-context"
import { ObservationDocument, useObservations } from "../../../../contexts/observations-context"
import { useLanguage } from "../../../../contexts/language-context"

type DeviceEventsCardProps = unknown
type DeviceHealthEvent = HealthDocument
type DeviceEvent = DeviceHealthEvent | ObservationDocument
type DeviceEventRowProps = {
    event: DeviceEvent
}

const FeedingObservationEvent = ({ event }: { event: ObservationDocument<"feeding"> }) => {
    const { L } = useLanguage()

    return (
        <tr>
            <td>
                <Center>
                    <ThemeIcon variant="light" size="md">
                        <IconBug size={18} />
                    </ThemeIcon>
                </Center>
            </td>
            <td>
                <Stack spacing={0}>
                    <Text>{L("Feed type brought in")}</Text>
                    <Text size="xs" color="dimmed">
                        {event.totalCount} {L("species")}:{" "}
                        {event.items
                            .sort((a, b) => b.count - a.count)
                            .map(item => `${item.count} ${item.species}`)
                            .join(", ")}
                    </Text>
                </Stack>
            </td>
            <td>
                <Center>
                    <Badge>{L("Observation").toLowerCase()}</Badge>
                </Center>
            </td>
            <td style={{ whiteSpace: "nowrap" }}>
                <Text color="dimmed">{event.timestamp.format("HH:mm:ss, DD/MM/YYYY")}</Text>
            </td>
        </tr>
    )
}

const UploadObservationEvent = ({ event }: { event: ObservationDocument<"upload"> }) => {
    const { L } = useLanguage()
    const message = event.message.trim()
    const content =
        event.status === "failed" ? (
            <Stack spacing={0}>
                <Text>{L("Failed upload")}</Text>
                <Text size="xs" color="dimmed">
                    {L("%s file(s) not uploaded", event.chunkSize)}
                </Text>
                {message ? (
                    <Text size="xs" color="red">
                        {event.message}
                    </Text>
                ) : null}
            </Stack>
        ) : (
            <Stack spacing={0}>
                <Text>{L("Upload")}</Text>
                <Text size="xs" color="dimmed">
                    {L("Uploaded %s file(s)", event.chunkSize)}
                </Text>
                {message ? (
                    <Text size="xs" color="dimmed">
                        {event.message}
                    </Text>
                ) : null}
            </Stack>
        )

    return (
        <tr>
            <td>
                <Center>
                    <ThemeIcon variant="light" size="md">
                        <IconUpload size={18} />
                    </ThemeIcon>
                </Center>
            </td>
            <td>{content}</td>
            <td>
                <Center>
                    <Badge>{L("Observation").toLowerCase()}</Badge>
                </Center>
            </td>
            <td style={{ whiteSpace: "nowrap" }}>
                <Text color="dimmed">{event.timestamp.format("HH:mm:ss, DD/MM/YYYY")}</Text>
            </td>
        </tr>
    )
}

const EntryObservationEvent = ({ event }: { event: ObservationDocument }) => {
    const { L } = useLanguage()

    return (
        <tr>
            <td>
                <Center>
                    <ThemeIcon variant="light" size="md">
                        <IconDoorEnter size={18} />
                    </ThemeIcon>
                </Center>
            </td>
            <td>
                <Text>{L("Entry")}</Text>
            </td>
            <td>
                <Center>
                    <Badge>{L("Observation").toLowerCase()}</Badge>
                </Center>
            </td>
            <td style={{ whiteSpace: "nowrap" }}>
                <Text color="dimmed">{event.timestamp.format("HH:mm:ss, DD/MM/YYYY")}</Text>
            </td>
        </tr>
    )
}

const HealthEvent = ({ event }: { event: DeviceHealthEvent }) => {
    const { L } = useLanguage()
    const storage = Array.isArray(event.storage) ? event.storage[0] : event.storage

    return (
        <tr>
            <td>
                <Center>
                    <ThemeIcon variant="light" size="md">
                        <IconActivity size={18} />
                    </ThemeIcon>
                </Center>
            </td>
            <td>
                <Stack spacing={0}>
                    <Text>{L("Device status")}</Text>
                    <Text size="xs" color="dimmed">
                        {event.status + ", "}
                        {typeof event.battery === "number" && L("Battery %s%", event.battery.toFixed(0)) + ", "}
                        {event.temp.toFixed(1)}°C{", "}
                        {L("%s used", formatBytes(storage))}
                        {event.network && ", " + event.network}
                    </Text>
                </Stack>
            </td>
            <td>
                <Center>
                    <Badge>{L("Health").toLowerCase()}</Badge>
                </Center>
            </td>
            <td style={{ whiteSpace: "nowrap" }}>
                <Text color="dimmed">{event.timestamp.format("HH:mm:ss, DD/MM/YYYY")}</Text>
            </td>
        </tr>
    )
}

const DeviceEventRow: FC<DeviceEventRowProps> = ({ event }) => {
    if (!("type" in event)) return <HealthEvent event={event} />

    if (event.type === "feeding") return <FeedingObservationEvent event={event as ObservationDocument<"feeding">} />
    if (event.type === "upload") return <UploadObservationEvent event={event as ObservationDocument<"upload">} />

    return <EntryObservationEvent event={event} />
}

const DeviceEventsCard: FC<DeviceEventsCardProps> = () => {
    const { data: health } = useHealth()
    const { data: observations } = useObservations()
    const { L } = useLanguage()
    const data = useMemo(
        () => [...health, ...observations].sort((a, b) => b.timestamp.valueOf() - a.timestamp.valueOf()),
        [health, observations],
    ).filter(event => event.id !== "latest")

    const [numberOfEvents, setNumberOfEvents] = useState(50)

    return (
        <Card withBorder>
            <Stack>
                <Group position="apart" noWrap>
                    <Text color="dimmed" weight="bold" sx={theme => ({ fontFamily: theme.headings.fontFamily })}>
                        <Group spacing="xs">
                            <IconTimelineEventText size={20} />
                            {L("Events")}
                        </Group>
                    </Text>
                </Group>
                <ScrollArea h="50vh" offsetScrollbars>
                    <Table>
                        <thead>
                            <tr>
                                <th style={{ width: "1%" }} />
                                <th>{L("Event")}</th>
                                <th style={{ textAlign: "center" }}>{L("Type")}</th>
                                <th style={{ width: "1%" }}>{L("Timestamp")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.slice(0, numberOfEvents).map(event => (
                                <DeviceEventRow key={event.id} event={event} />
                            ))}
                            {numberOfEvents < data.length ? (
                                <tr>
                                    <td colSpan={100} align={"center"}>
                                        <Button variant="subtle" onClick={() => setNumberOfEvents(numberOfEvents + 50)}>Show more</Button>
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </Table>
                </ScrollArea>
            </Stack>
        </Card>
    )
}

export default DeviceEventsCard
